import { motion, AnimatePresence, useAnimation } from "framer-motion";
import { Icon } from "./Icon";
import { useBookmarkContext } from "../hooks/useBookmarkContext";
import { useEffect } from "react";

interface PinnedBookmarkButtonProps {
  onClick: () => void;
}

export function PinnedBookmarkButton({ onClick }: PinnedBookmarkButtonProps) {
  const { bookmarks } = useBookmarkContext();
  const controls = useAnimation();

  // Count active bookmarks
  const bookmarkCount = Object.values(bookmarks).filter(Boolean).length;

  // Initial animation and pulse animation when bookmark count changes
  useEffect(() => {
    controls.start({ opacity: 1, scale: 1 });
  }, [controls]);

  useEffect(() => {
    if (bookmarkCount > 0) {
      controls.start({
        scale: [1, 1.2, 1],
        transition: {
          duration: 0.4,
          ease: "easeInOut",
        },
      });
    }
  }, [bookmarkCount, controls]);

  return (
    <motion.button
      onClick={onClick}
      className="relative cursor-pointer w-14 h-14 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-all duration-200 group shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={controls}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      aria-label={`View ${bookmarkCount} bookmarks`}
      data-pinned-bookmark-button
    >
      <Icon
        icon="bookmark"
        className="text-white/90 text-2xl group-hover:text-white transition-colors"
      />

      {/* Badge with count */}
      <AnimatePresence>
        {bookmarkCount > 0 && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 500, damping: 25 }}
            className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 bg-emerald-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg"
          >
            {bookmarkCount}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
}
